import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesService } from '../services/api';
import { AppNav } from '../components/AppNav';
import { NoAgencyBanner } from '../components/NoAgencyBanner';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/lib/utils';
import {
  Search, BedDouble, Bath,
  Maximize2, CheckCircle2, MapPin, SlidersHorizontal,
} from 'lucide-react';

interface Property {
  id: string;
  title: string;
  price: number;
  currency?: string;
  address?: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  lat: number;
  lng: number;
  images?: string[];
  description?: string;
  property_type?: string;
}

function PropertySkeleton() {
  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden animate-pulse">
      <div className="h-1 bg-stone-200" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-stone-100 rounded-md w-3/4" />
        <div className="h-7 bg-stone-100 rounded-md w-1/2" />
        <div className="flex gap-4 pt-1">
          <div className="h-3 bg-stone-100 rounded-md w-14" />
          <div className="h-3 bg-stone-100 rounded-md w-14" />
          <div className="h-3 bg-stone-100 rounded-md w-14" />
        </div>
      </div>
    </div>
  );
}

function PropertiesPage() {
  const navigate = useNavigate(); // used for client-views navigation
  const [query, setQuery] = useState(() => localStorage.getItem('prop_filter_q') ?? '');
  const [priceMin, setPriceMin] = useState(() => localStorage.getItem('prop_filter_priceMin') ?? '');
  const [priceMax, setPriceMax] = useState(() => localStorage.getItem('prop_filter_priceMax') ?? '');
  const [bedrooms, setBedrooms] = useState(() => localStorage.getItem('prop_filter_bedrooms') ?? '');
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [noAgency, setNoAgency] = useState(false);

  useEffect(() => { document.title = 'Propiedades — Takarah'; }, []);

  const handleSearch = async () => {
    setLoading(true);
    setSearchError('');
    setSearched(true);
    localStorage.setItem('prop_filter_q', query);
    localStorage.setItem('prop_filter_priceMin', priceMin);
    localStorage.setItem('prop_filter_priceMax', priceMax);
    localStorage.setItem('prop_filter_bedrooms', bedrooms);
    try {
      const filters: Record<string, unknown> = { q: query };
      if (priceMin) filters.price_min = parseFloat(priceMin);
      if (priceMax) filters.price_max = parseFloat(priceMax);
      if (bedrooms) filters.bedrooms_min = parseInt(bedrooms);
      const response = await propertiesService.search(filters);
      setProperties(response.items);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setNoAgency(true);
      } else {
        setSearchError('Error al buscar propiedades. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const activeFilters = [priceMin, priceMax, bedrooms].filter(Boolean).length;

  if (noAgency) return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <NoAgencyBanner />
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-7">
          <h1 className="text-2xl font-semibold tracking-tight">Propiedades</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Busca y cura selecciones para tus clientes
          </p>
        </div>

        {/* Search bar */}
        <div className="bg-white border border-border rounded-xl p-4 mb-5">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Buscar propiedades..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-9 pr-3 py-2 text-sm bg-secondary/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                showFilters || activeFilters > 0
                  ? 'border-primary/40 bg-primary/5 text-primary'
                  : 'border-border bg-secondary/40 text-muted-foreground hover:text-foreground'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filtros</span>
              {activeFilters > 0 && (
                <span className="w-4 h-4 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center">
                  {activeFilters}
                </span>
              )}
            </button>
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="transition-all active:scale-[0.98]"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border">
              <input
                type="number"
                placeholder="Precio mín."
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                className="w-36 px-3 py-2 text-sm bg-secondary/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Precio máx."
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                className="w-36 px-3 py-2 text-sm bg-secondary/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Recámaras mín."
                value={bedrooms}
                onChange={(e) => setBedrooms(e.target.value)}
                className="w-36 px-3 py-2 text-sm bg-secondary/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              {activeFilters > 0 && (
                <button
                  type="button"
                  onClick={() => { setPriceMin(''); setPriceMax(''); setBedrooms(''); localStorage.removeItem('prop_filter_priceMin'); localStorage.removeItem('prop_filter_priceMax'); localStorage.removeItem('prop_filter_bedrooms'); }}
                  className="px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          )}

          {searchError && (
            <p className="mt-3 text-sm text-destructive">{searchError}</p>
          )}
        </div>

        {/* Selection bar */}
        {selected.size > 0 && (
          <div className="mb-5 flex items-center justify-between bg-primary/10 border border-primary/20 rounded-xl px-5 py-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                {selected.size} {selected.size === 1 ? 'propiedad seleccionada' : 'propiedades seleccionadas'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Limpiar
              </button>
              <Button
                size="sm"
                onClick={() => navigate(`/client-views/new?properties=${Array.from(selected).join(',')}`)}
                className="transition-all active:scale-[0.98]"
              >
                Crear selección
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => <PropertySkeleton key={i} />)}
          </div>
        ) : properties.length > 0 ? (
          <>
            <p className="text-xs text-muted-foreground mb-4">
              {properties.length} {properties.length === 1 ? 'resultado' : 'resultados'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {properties.map((property) => {
                const isSelected = selected.has(property.id);
                return (
                  <button
                    key={property.id}
                    onClick={() => toggleSelect(property.id)}
                    className={`group text-left w-full bg-white rounded-xl border transition-all duration-200 overflow-hidden hover:shadow-md active:scale-[0.99] ${
                      isSelected
                        ? 'border-primary/50 shadow-md shadow-primary/8 ring-1 ring-primary/20'
                        : 'border-border hover:border-stone-200 hover:shadow-stone-100'
                    }`}
                  >
                    <div className="flex">
                      {/* Side accent */}
                      <div
                        className={`w-[3px] self-stretch flex-shrink-0 transition-colors duration-200 rounded-l-xl ${
                          isSelected ? 'bg-primary' : 'bg-stone-100 group-hover:bg-stone-200'
                        }`}
                      />
                      {property.images && property.images.length > 0 && (
                        <div className="w-28 shrink-0 overflow-hidden">
                          <img
                            src={resolveImageUrl(property.images[0])}
                            alt={property.title}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <div className="p-5 flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-medium text-sm leading-snug line-clamp-2 flex-1">
                            {property.title}
                          </h3>
                          {isSelected && (
                            <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          )}
                        </div>
                        {property.property_type && (
                          <span className="text-[10px] text-muted-foreground capitalize font-medium">
                            {property.property_type}
                          </span>
                        )}
                        {property.address && (
                          <p className="text-[11px] text-muted-foreground/70 mb-2.5 flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 shrink-0" />
                            {property.address}
                          </p>
                        )}
                        <p className="text-2xl font-semibold tracking-tight text-primary mb-3 leading-none">
                          ${property.price.toLocaleString('es-MX')}
                          <span className="text-[11px] font-normal text-muted-foreground ml-1.5 tracking-normal">
                            {property.currency ?? 'MXN'}
                          </span>
                        </p>
                        {property.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {property.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <BedDouble className="w-3.5 h-3.5" />
                            {property.bedrooms} rec.
                          </span>
                          <span className="flex items-center gap-1">
                            <Bath className="w-3.5 h-3.5" />
                            {property.bathrooms} baños
                          </span>
                          <span className="flex items-center gap-1">
                            <Maximize2 className="w-3.5 h-3.5" />
                            {property.area} m²
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : searched ? (
          <div className="text-center py-24">
            <MapPin className="w-10 h-10 mx-auto mb-4 text-muted-foreground/30" />
            <p className="font-medium text-muted-foreground">Sin resultados</p>
            <p className="text-sm text-muted-foreground/60 mt-1">Prueba con otros filtros de búsqueda</p>
          </div>
        ) : (
          <div className="text-center py-24">
            <Search className="w-10 h-10 mx-auto mb-4 text-muted-foreground/30" />
            <p className="font-medium text-muted-foreground">Busca propiedades para empezar</p>
            <p className="text-sm text-muted-foreground/60 mt-1">Usa la barra de arriba para encontrar propiedades</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default PropertiesPage;
